import { useEffect, useRef, useState } from 'react';
import styles from './PersonaCarousel.module.css';

export interface PersonaFrame {
  r2Key: string;
  url: string;
  label?: string;
}

export type PersonaCarouselTier = 'mini' | 'max';

export interface PersonaCarouselLabels {
  overlay: string;
  ctaMinipack: string;
  chipMax?: string;
  chipSavePreviews?: string;
  generating?: string;
  badgeFormat?: (current: number, total: number) => string;
}

export interface PersonaCarouselProps {
  frames: PersonaFrame[];
  tier?: PersonaCarouselTier;
  labels: PersonaCarouselLabels;
  selectedIndex?: number;
  onSelectFrame?: (index: number) => void;
  onCtaPress?: () => void;
  onChipMaxPress?: () => void;
  onChipSavePreviewsPress?: () => void;
}

const DEFAULT_BADGE = (current: number, total: number): string =>
  `${String(current).padStart(2, '0')}/${String(total).padStart(2, '0')}`;

export function PersonaCarousel({
  frames,
  tier = 'mini',
  labels,
  selectedIndex,
  onSelectFrame,
  onCtaPress,
  onChipMaxPress,
  onChipSavePreviewsPress,
}: PersonaCarouselProps): JSX.Element {
  const [focusedIndex, setFocusedIndex] = useState<number>(selectedIndex ?? 0);
  const trackRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Observer reads through this ref to avoid stale-closure on focusedIndex.
  const focusedRef = useRef<number>(selectedIndex ?? 0);
  // Suppresses the IntersectionObserver while a programmatic scroll (from a
  // tap or external selectedIndex change) is settling — otherwise observer
  // immediately reverts the user's choice.
  const suppressUntilRef = useRef<number>(0);
  useEffect(() => { focusedRef.current = focusedIndex; }, [focusedIndex]);

  const setFocus = (i: number): void => {
    suppressUntilRef.current = Date.now() + 600;
    focusedRef.current = i;
    setFocusedIndex(i);
  };

  useEffect(() => {
    if (typeof selectedIndex === 'number' && selectedIndex !== focusedRef.current) {
      setFocus(selectedIndex);
      const el = cardRefs.current[selectedIndex];
      el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const track = trackRef.current;
    if (!track || frames.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressUntilRef.current) return;
        let bestIdx = focusedRef.current;
        let bestRatio = 0;
        for (const entry of entries) {
          if (entry.intersectionRatio > bestRatio) {
            const idx = cardRefs.current.findIndex((el) => el === entry.target);
            if (idx >= 0) {
              bestRatio = entry.intersectionRatio;
              bestIdx = idx;
            }
          }
        }
        if (bestRatio > 0 && bestIdx !== focusedRef.current) {
          focusedRef.current = bestIdx;
          setFocusedIndex(bestIdx);
          onSelectFrame?.(bestIdx);
        }
      },
      { root: track, threshold: [0.5, 0.75, 1.0] },
    );
    cardRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [frames.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = frames.length || 3;
  const badgeFormat = labels.badgeFormat ?? DEFAULT_BADGE;
  const isEmpty = frames.length === 0;
  const visibleItems: Array<PersonaFrame | null> = isEmpty
    ? [null, null, null]
    : frames;

  return (
    <div className={styles.wrap}>
      <div
        ref={trackRef}
        className={styles.track}
        role="group"
        aria-roledescription="carousel"
        aria-label={labels.overlay}
      >
        {visibleItems.map((frame, i) => {
          const isFocused = i === focusedIndex;
          return (
            <button
              key={frame?.r2Key ?? `skeleton-${i}`}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              type="button"
              className={`${styles.card} ${isFocused ? styles.card_active : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} / ${total}`}
              onClick={() => {
                setFocus(i);
                onSelectFrame?.(i);
                cardRefs.current[i]?.scrollIntoView({
                  behavior: 'smooth',
                  inline: 'center',
                  block: 'nearest',
                });
              }}
            >
              {frame ? (
                <img
                  src={frame.url}
                  alt={frame.label ?? `Натальный портрет ${i + 1}`}
                  className={styles.img}
                  loading="lazy"
                />
              ) : (
                <div className={styles.skeleton}>
                  <span className={styles.skeleton_text}>
                    {labels.generating ?? 'Generating…'}
                  </span>
                </div>
              )}
              <div className={styles.watermark} aria-hidden="true">
                {labels.overlay}
              </div>
              {isFocused && (
                <div className={styles.badge} aria-live="polite" aria-atomic="true">
                  {badgeFormat(i + 1, total)}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className={styles.dots} aria-hidden="true">
        {visibleItems.map((_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i === focusedIndex ? styles.dot_active : ''}`}
          />
        ))}
      </div>

      <button
        type="button"
        className={styles.ctaPrimary}
        onClick={onCtaPress}
        disabled={isEmpty}
      >
        {labels.ctaMinipack}
      </button>

      {(labels.chipMax || labels.chipSavePreviews) && (
        <div className={styles.chips}>
          {labels.chipMax && (
            <button
              type="button"
              className={`${styles.chip} ${tier === 'max' ? styles.chip_active : ''}`}
              onClick={onChipMaxPress}
            >
              {labels.chipMax}
            </button>
          )}
          {labels.chipSavePreviews && (
            <button
              type="button"
              className={styles.chip}
              onClick={onChipSavePreviewsPress}
            >
              {labels.chipSavePreviews}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
